import { useLazyQuery } from "@apollo/client/react";
import { useId, useState } from "react";
import { useNavigate } from "react-router";

import rethrowIfNotAborted from "apollo/rethrowIfNotAborted";
import DropdownHeaderSearch from "ds/components/Dropdown/Header/Search";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import SplitControlButton from "ds/components/SplitControl/Button";
import SplitControlDropdown from "ds/components/SplitControl/Dropdown";
import SplitControlWrapper from "ds/components/SplitControl/Wrapper";
import { BlueprintState, SearchQueryOrderDirection } from "types/generated";
import useCanCreateTemplateDeployment from "shared/Templates/access/useCanCreateTemplateDeployment";

import { getTemplateMarketplaceDeployFormUrl } from "../../utils";
import { GET_TEMPLATE_VERSIONS_FOR_DEPLOY } from "../gql";
import styles from "./styles.module.css";

type TemplateCardDeployButtonProps = {
  templateId: string;
  latestVersionId: string;
};

const TemplateCardDeployButton = ({
  templateId,
  latestVersionId,
}: TemplateCardDeployButtonProps) => {
  const navigate = useNavigate();
  const canDeployTemplate = useCanCreateTemplateDeployment();
  const [searchQuery, setSearchQuery] = useState("");
  const menuId = useId();

  const deploymentFormUrl = getTemplateMarketplaceDeployFormUrl(templateId, latestVersionId);

  const [fetchVersions, { data: versionsData, loading: versionsLoading }] = useLazyQuery(
    GET_TEMPLATE_VERSIONS_FOR_DEPLOY
  );

  const publishedVersions =
    versionsData?.template?.searchTemplateVersions?.edges
      .map((edge) => edge.node)
      .filter((v) => !v.deprecatedAt) ?? [];

  const filteredVersions = searchQuery
    ? publishedVersions.filter((v) => v.version?.toLowerCase().includes(searchQuery.toLowerCase()))
    : publishedVersions;

  const handleDropdownOpenChange = (open: boolean) => {
    if (open) {
      fetchVersions({
        variables: {
          id: templateId,
          input: {
            first: 50,
            after: null,
            fullTextSearch: null,
            orderBy: [{ field: "createdAt", direction: SearchQueryOrderDirection.Desc }],
            requestedPage: null,
            predicates: [
              {
                field: "state",
                exclude: null,
                constraint: {
                  enumEquals: [BlueprintState.Published],
                  booleanEquals: null,
                  hierarchyNode: null,
                  hierarchyNodeValueEquals: null,
                  stringEquals: null,
                  stringMatches: null,
                  timeInLast: null,
                  timeInRange: null,
                },
              },
            ],
          },
        },
      }).catch(rethrowIfNotAborted);
    } else {
      setSearchQuery("");
    }
  };

  return (
    <SplitControlWrapper variant="secondary" size="medium">
      <SplitControlButton
        tooltip={canDeployTemplate.message ?? "Deploy latest version"}
        disabled={!canDeployTemplate.result}
        onPress={() => navigate(deploymentFormUrl)}
      >
        Deploy
      </SplitControlButton>
      <SplitControlDropdown
        tooltip={canDeployTemplate.message ?? "Choose version"}
        disabled={!canDeployTemplate.result}
        placement="bottom right"
        onOpenChange={handleDropdownOpenChange}
        id={menuId}
        header={
          <DropdownHeaderSearch
            className={styles.versionSearch}
            listboxId={menuId}
            searchValue={searchQuery}
            setSearchValue={setSearchQuery}
            searchPlaceholder="Search versions..."
          />
        }
      >
        {versionsLoading && <DropdownMenuItem isDisabled>Loading...</DropdownMenuItem>}
        {!versionsLoading && filteredVersions.length === 0 && (
          <DropdownMenuItem isDisabled>
            {searchQuery ? "No versions match your search" : "No versions available"}
          </DropdownMenuItem>
        )}
        {!versionsLoading &&
          filteredVersions.map((version) => (
            <DropdownMenuItem
              key={version.id}
              href={getTemplateMarketplaceDeployFormUrl(templateId, version.id)}
            >
              {version.id === latestVersionId ? `${version.version} (latest)` : version.version}
            </DropdownMenuItem>
          ))}
      </SplitControlDropdown>
    </SplitControlWrapper>
  );
};

export default TemplateCardDeployButton;
